// Der Weltgenerator von Paket 2: das Dorf aus `village.ts` in der Mitte, vier gepflasterte Wege
// nach außen und daran die Hof-Parzellen aus `parcels.ts`, jede mit Zaun, Tor, Schild, Unterstand,
// Hühnerstall, Apfelbaum, Feld, Kiste und einer Kuh auf der Weide
// (docs/architektur-paket-2.md, Abschnitt „Welt mit Parzellen“, ADR 0023).
//
// Deterministisch wie `generateVillage()`: derselbe Parameter ergibt Byte für Byte dieselbe Welt,
// nichts wird gewürfelt. `generateVillage()` bleibt daneben bestehen – es ist die Welt der
// Technik-Demo und die Vorlage für die Mitte.
//
// Reine Daten, kein DOM, kein Rendering: Der Client baut daraus die Szene, der Server die
// Parzellenvergabe und die Höfe.

import { BLOCK } from './blocks.js';
import { createWorld, setBlock, type World } from './chunk.js';
import {
  DEFAULT_WORLD_PARAMS,
  DORF_VERSATZ,
  HOF_PLAN,
  WEGE,
  WELT_CHUNKS,
  parcelLayout,
  parcelLocalRect,
  parcelLocalToWorld,
  parcelPen,
  parcelPrefabDrehung,
  type Parcel,
  type Punkt,
  type Rechteck,
  type WorldParams,
} from './parcels.js';
import { PREFABS, PREFAB_LEGEND, drawFence, findPrefabCells, rotatePrefab, stampPrefab, type PrefabName } from './prefabs.js';
import type { AnimalSpawn, Landmark, Pen, Village } from './types.js';
import { BODEN, FUSS_Y, baueDorf, fuelleBoden, pflanzeHecke } from './village.js';

/** Die Tierart, die auf jeder frischen Parzelle steht. Eine je Hof, Kennung `<parzelle>-kuh-1`. */
const START_TIER = { species: 'kuh', nummer: 1 } as const;

/** Die fertige Welt: ein Dorf wie bisher, dazu die Parzellen und die Parameter, die sie erzeugten. */
export interface GameWorld extends Village {
  params: WorldParams;
  parcels: Parcel[];
}

/** Kennung der Welt zu einem Parametersatz; sie ändert sich mit der Parzellenzahl. */
export function weltKennung(params: WorldParams): string {
  return `dorf-v2-${params.parcels}`;
}

/**
 * Erzeugt die Welt mit Parzellen. Jeder Aufruf liefert eine neue, aber Byte für Byte gleiche
 * Welt; `params.parcels` bestimmt, wie viele Höfe entstehen (0 bis `MAX_PARCELS`).
 */
export function generateWorld(params: WorldParams = DEFAULT_WORLD_PARAMS): GameWorld {
  const parcels = parcelLayout(params);

  const world = createWorld(WELT_CHUNKS, WELT_CHUNKS);
  fuelleBoden(world);
  pflanzeHecke(world);
  // Erst die Wege, dann das Dorf: Der Dorfbauplan legt seinen eigenen Weg und den Platz noch
  // einmal darüber und darf dabei nichts vom Dorf verlieren.
  pflastereWege(world);
  const dorf = baueDorf(world, DORF_VERSATZ, DORF_VERSATZ, { mitKoppel: false });

  const pens: Pen[] = [];
  const animals: AnimalSpawn[] = [];
  const landmarks: Landmark[] = [...dorf.landmarks];
  for (const parcel of parcels) {
    landmarks.push(...baueParzelle(world, parcel));
    pens.push(parcelPen(parcel));
    animals.push(startTier(parcel));
  }

  return {
    world,
    spawn: dorf.spawn,
    pens,
    animals,
    landmarks,
    worldId: weltKennung(params),
    params: { ...params },
    parcels,
  };
}

/** Das Zeichen, mit dem die Bauteile einen Apfel zeichnen. */
const APFEL_ZEICHEN = Object.keys(PREFAB_LEGEND).find((zeichen) => PREFAB_LEGEND[zeichen] === 'apfel') ?? 'A';

/**
 * Die Zellen, in denen der Apfelbaum einer Parzelle Äpfel tragen kann, in Weltkoordinaten und in
 * fester Reihenfolge (Ebene, Zeile, Spalte des gedrehten Bauteils). Der Generator setzt dort
 * `apfel`. Der Client zeigt davon so viele, wie `FarmState.tree.apples` sagt, und setzt die übrigen
 * auf `laub` – ohne Apfel ist die Krone die des gewöhnlichen Baums (task-014, ADR 0031).
 */
export function parcelApfelZellen(parcel: Parcel): Punkt[] {
  const teil = rotatePrefab(PREFABS.apfelbaum, parcelPrefabDrehung(parcel));
  const rechteck = lokalesRechteck(parcel, HOF_PLAN.apfelbaum);
  return findPrefabCells(teil, APFEL_ZEICHEN).map((zelle) => ({
    x: rechteck.minX + zelle.x,
    y: FUSS_Y + zelle.y,
    z: rechteck.minZ + zelle.z,
  }));
}

/** Pflastert die beiden Wegstreifen, die sich auf dem Dorfplatz kreuzen. */
function pflastereWege(world: World): void {
  for (const weg of WEGE) fuelleRechteck(world, weg, BODEN.oberflaeche, BLOCK.pflaster);
}

/**
 * Setzt alles, was zu einer Parzelle gehört, in die Welt und liefert deren Orientierungspunkte.
 * Reihenfolge: Zaun und Tor, dann der Boden (Pfad und Feld), dann die Bauteile, zuletzt die
 * einzelnen Blöcke – so überschreibt kein späterer Schritt einen früheren.
 */
function baueParzelle(world: World, parcel: Parcel): Landmark[] {
  zaunMitTor(world, parcel);

  for (let u = HOF_PLAN.pfad.uVon; u <= HOF_PLAN.pfad.uBis; u++) {
    setzeLokal(world, parcel, u, HOF_PLAN.pfad.v, BODEN.oberflaeche, BLOCK.pflaster);
  }
  fuelleRechteck(world, lokalesRechteck(parcel, HOF_PLAN.feld), BODEN.oberflaeche, BLOCK.acker);

  stampeBauteil(world, parcel, 'unterstand', HOF_PLAN.unterstand);
  stampeBauteil(world, parcel, 'huehnerstall', HOF_PLAN.huehnerstall);
  stampeBauteil(world, parcel, 'apfelbaum', HOF_PLAN.apfelbaum);

  setBlock(world, parcel.sign.x, FUSS_Y, parcel.sign.z, BLOCK.schild);
  setBlock(world, parcel.chest.x, FUSS_Y, parcel.chest.z, BLOCK.kiste);

  return [
    { kind: 'tor', x: parcel.gate.x, y: parcel.y, z: parcel.gate.z },
    { kind: 'schild', x: parcel.sign.x, y: parcel.y, z: parcel.sign.z },
    { kind: 'kiste', x: parcel.chest.x, y: parcel.y, z: parcel.chest.z },
    { kind: 'unterstand', x: parcel.shelter.x, y: parcel.y, z: parcel.shelter.z },
    { kind: 'apfelbaum', x: parcel.tree.x, y: parcel.y, z: parcel.tree.z },
    lokalesLandmark(parcel, 'huehnerstall', HOF_PLAN.huehnerstall),
    lokalesLandmark(parcel, 'feld', HOF_PLAN.feld),
  ];
}

/** Zaun rund um die Parzelle, das Tor auf seiner Zelle in der Zaunlinie. */
function zaunMitTor(world: World, parcel: Parcel): void {
  drawFence(world, parcel.minX, parcel.minZ, parcel.maxX, parcel.minZ, FUSS_Y);
  drawFence(world, parcel.minX, parcel.maxZ, parcel.maxX, parcel.maxZ, FUSS_Y);
  drawFence(world, parcel.minX, parcel.minZ, parcel.minX, parcel.maxZ, FUSS_Y);
  drawFence(world, parcel.maxX, parcel.minZ, parcel.maxX, parcel.maxZ, FUSS_Y);
  setBlock(world, parcel.gate.x, FUSS_Y, parcel.gate.z, BLOCK.tor);
}

/**
 * Stempelt ein Bauteil in die Parzelle. Alle Hof-Bauteile sind mit ihrer Vorderseite nach Süden
 * gezeichnet; `parcelPrefabDrehung` dreht sie so, dass diese Seite zum Tor blickt. Das gedrehte
 * Bauteil muss dann genau das geplante Rechteck ausfüllen – passt es nicht, stimmt der Bauplan
 * nicht mit den Maßen des Bauteils überein, und das soll sofort auffallen.
 */
function stampeBauteil(world: World, parcel: Parcel, name: PrefabName, plan: LokalesRechteck): void {
  const teil = rotatePrefab(PREFABS[name], parcelPrefabDrehung(parcel));
  const rechteck = lokalesRechteck(parcel, plan);
  const breite = rechteck.maxX - rechteck.minX + 1;
  const tiefe = rechteck.maxZ - rechteck.minZ + 1;
  if (teil.size[0] !== breite || teil.size[2] !== tiefe) {
    throw new Error(
      `Bauteil ${name} ist ${teil.size[0]} × ${teil.size[2]} Blöcke groß, der Hofplan sieht ${breite} × ${tiefe} vor.`,
    );
  }
  stampPrefab(world, teil, PREFAB_LEGEND, rechteck.minX, FUSS_Y, rechteck.minZ);
}

/** Die Kuh, die auf einer frischen Parzelle steht: in der Mitte ihrer Weidezelle. */
function startTier(parcel: Parcel): AnimalSpawn {
  const zelle = parcelLocalToWorld(parcel, HOF_PLAN.weide.u, HOF_PLAN.weide.v);
  return {
    id: `${parcel.id}-${START_TIER.species}-${START_TIER.nummer}`,
    species: START_TIER.species,
    x: zelle.x + 0.5,
    y: parcel.y,
    z: zelle.z + 0.5,
    penId: parcel.penId,
  };
}

/** Ein Rechteck im lokalen Bauplan einer Parzelle (u vom Tor nach hinten, v quer dazu). */
interface LokalesRechteck {
  uMin: number;
  uMax: number;
  vMin: number;
  vMax: number;
}

function lokalesRechteck(parcel: Parcel, plan: LokalesRechteck): Rechteck {
  return parcelLocalRect(parcel, plan.uMin, plan.uMax, plan.vMin, plan.vMax);
}

/** Ein Orientierungspunkt in der Mitte eines lokalen Rechtecks; bei gerader Kantenlänge abgerundet. */
function lokalesLandmark(parcel: Parcel, kind: Landmark['kind'], plan: LokalesRechteck): Landmark {
  const zelle = parcelLocalToWorld(
    parcel,
    Math.floor((plan.uMin + plan.uMax) / 2),
    Math.floor((plan.vMin + plan.vMax) / 2),
  );
  return { kind, x: zelle.x, y: parcel.y, z: zelle.z };
}

/** Setzt einen Block an einer lokalen Zelle der Parzelle. */
function setzeLokal(world: World, parcel: Parcel, u: number, v: number, y: number, id: number): void {
  const zelle = parcelLocalToWorld(parcel, u, v);
  setBlock(world, zelle.x, y, zelle.z, id);
}

/** Füllt ein Rechteck auf einer Höhe mit einer Block-id. */
function fuelleRechteck(world: World, r: Rechteck, y: number, id: number): void {
  for (let z = r.minZ; z <= r.maxZ; z++) {
    for (let x = r.minX; x <= r.maxX; x++) setBlock(world, x, y, z, id);
  }
}
